import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { inr, fdate, fdt } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { StatusBadge } from "@/components/pawkt/StatusBadge";
import { FormModal } from "@/components/pawkt/FormModal";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BedDouble, Plus, LogIn, LogOut, DoorOpen, PawPrint } from "lucide-react";
import { ContextPanel } from "@/components/pawkt/ContextPanel";
import { ReallocateButton } from "@/components/pawkt/ReallocateButton";
import { EditUnitButton, UnitStatusButton, StayRelocateButton } from "@/components/pawkt/UnitActions";

const TONE = { occupied: ["border-amber-200 bg-amber-50/50", "text-amber-600"], available: ["border-emerald-200 bg-emerald-50/40", "text-emerald-600"],
  maintenance: ["border-slate-300 bg-slate-100", "text-slate-600"], unavailable: ["border-rose-200 bg-rose-50/50", "text-rose-600"] };
import { toast } from "sonner";

function Kpi({ label, value, accent = "slate", testid }) {
  const map = { slate: "text-slate-900", sky: "text-sky-700", amber: "text-amber-600", emerald: "text-emerald-700" };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular ${map[accent]}`}>{value}</p>
    </div>
  );
}

export default function Boarding() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = hasPerm(user, "boarding.edit");
  const [resOpen, setResOpen] = useState(false);
  const [kennelOpen, setKennelOpen] = useState(false);
  const [tab, setTab] = useState("occupancy");
  const [ctx, setCtx] = useState(null);

  const { data: dash } = useQuery({ queryKey: ["board-dash"], queryFn: async () => (await api.get("/boarding/dashboard")).data });
  const { data: stays, isLoading } = useQuery({ queryKey: ["board-stays"], queryFn: async () => (await api.get("/boarding/stays")).data });
  const { data: board } = useQuery({ queryKey: ["board-occ"], queryFn: async () => (await api.get("/boarding/board")).data });
  const { data: resources } = useQuery({ queryKey: ["board-res"], queryFn: async () => (await api.get("/boarding/resources")).data });
  const { data: pets } = useQuery({ queryKey: ["pets-min"], queryFn: async () => (await api.get("/pets?limit=200")).data.items });

  const petOpts = (pets || []).map((p) => ({ value: p.id, label: `${p.name} · ${p.breed || p.species || "Pet"}${p.pet_code ? ` · ${p.pet_code}` : ""}${p.owner_name ? ` · ${p.owner_name}` : ""}` }));
  const resOpts = (resources?.items || []).map((r) => ({ value: r.id, label: r.name }));

  const refresh = () => ["board-dash", "board-stays", "board-occ"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const createStay = useMutation({ mutationFn: (v) => api.post("/boarding/stays", { ...v, status: "reserved" }), onSuccess: () => { refresh(); setResOpen(false); setTab("reservations"); toast.success("Reservation created"); }, onError: (e) => toast.error(apiErr(e)) });
  const addKennel = useMutation({ mutationFn: (v) => api.post("/boarding/resources", v), onSuccess: () => { qc.invalidateQueries({ queryKey: ["board-res"] }); qc.invalidateQueries({ queryKey: ["board-occ"] }); setKennelOpen(false); toast.success("Resource added"); }, onError: (e) => toast.error(apiErr(e)) });
  const checkIn = useMutation({ mutationFn: (id) => api.post(`/boarding/stays/${id}/check-in`, {}), onSuccess: () => { refresh(); toast.success("Checked in"); }, onError: (e) => toast.error(apiErr(e)) });
  const checkOut = useMutation({ mutationFn: (id) => api.post(`/boarding/stays/${id}/check-out`, {}), onSuccess: () => { refresh(); toast.success("Checked out"); }, onError: (e) => toast.error(apiErr(e)) });

  const d = dash || {};
  const rows = stays?.items || [];
  const petName = (id) => (pets || []).find((p) => p.id === id)?.name || id;

  return (
    <div>
      <PageHeader title="Boarding" subtitle="Reservations, check-in/out & live occupancy">
        {canEdit && <Button variant="outline" onClick={() => setKennelOpen(true)} data-testid="board-add-resource-btn" className="gap-1.5"><Plus className="h-4 w-4" /> Resource</Button>}
        {canEdit && <Button onClick={() => setResOpen(true)} data-testid="board-new-reservation-btn" className="bg-slate-900 text-white gap-1.5"><Plus className="h-4 w-4" /> New Reservation</Button>}
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" data-testid="board-dashboard">
        <Kpi label="Occupancy" value={`${d.occupancy_pct ?? 0}%`} accent="sky" testid="board-kpi-occupancy" />
        <Kpi label="Occupied" value={d.occupied_resources ?? 0} testid="board-kpi-occupied" />
        <Kpi label="Available" value={d.available_resources ?? 0} accent="emerald" testid="board-kpi-available" />
        <Kpi label="Check-ins" value={d.check_ins_today ?? 0} testid="board-kpi-checkins" />
        <Kpi label="Check-outs" value={d.check_outs_today ?? 0} accent="amber" testid="board-kpi-checkouts" />
        <Kpi label="Current" value={d.current_stays ?? 0} testid="board-kpi-current" />
        <Kpi label="Upcoming" value={d.upcoming_reservations ?? 0} testid="board-kpi-upcoming" />
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="occupancy" data-testid="board-tab-occupancy">Occupancy Board</TabsTrigger>
            <TabsTrigger value="reservations" data-testid="board-tab-reservations">Stays ({rows.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="occupancy" className="mt-4">
            {(board?.resources || []).length === 0 ? <Empty icon={DoorOpen} title="No resources" subtitle="Add kennels or rooms to manage occupancy." /> : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="board-occupancy-grid">
                {(board?.resources || []).map((r) => (
                  <div key={r.resource_id} data-testid={`board-resource-${r.resource_id}`} className={`rounded-xl border p-4 ${(TONE[r.status] || TONE.available)[0]}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-800 truncate">{r.name} <span className="text-[11px] font-normal capitalize text-slate-400">{(r.type || "").replace("_", " ")}</span></span>
                      <span className="flex items-center gap-1">
                        <span data-testid={`board-resource-status-${r.resource_id}`} className={`text-xs font-semibold uppercase ${(TONE[r.status] || TONE.available)[1]}`}>{r.status} · {r.occupied_pets ?? 0}/{r.capacity ?? 1}</span>
                        {canEdit && <EditUnitButton unit={r} />}
                        {canEdit && <UnitStatusButton unit={r} />}
                      </span>
                    </div>
                    {r.status_reason && <p className="mt-1 text-[11px] text-slate-500" data-testid={`board-resource-reason-${r.resource_id}`}>{r.status_reason}</p>}
                    {r.occupants?.length ? r.occupants.map((o) => (
                      <div key={o.stay_id} className="mt-2 text-xs text-slate-600" data-testid={`board-occupant-${o.stay_id}`}>
                        <div className="font-medium text-slate-800">{(o.pets || []).map((p) => p.name).filter(Boolean).join(", ") || o.pet_name || petName(o.pet_id)}</div>
                        <div>In: {fdate(o.check_in)} · Out: {fdate(o.expected_check_out)}</div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => checkOut.mutate(o.stay_id)} data-testid={`board-checkout-${o.stay_id}`} className="h-7 gap-1 text-xs"><LogOut className="h-3 w-3" /> Check out</Button>
                          {o.booking_id && canEdit && <ReallocateButton bookingId={o.booking_id} onDone={refresh} />}
                        </div>
                      </div>
                    )) : <p className="mt-2 text-xs text-slate-400">Available</p>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reservations" className="mt-4">
            {isLoading ? <Loading /> : rows.length === 0 ? <Empty icon={BedDouble} title="No stays" subtitle="Create a reservation to get started." /> : (
              <div className="divide-y divide-slate-100" data-testid="board-stays-list">
                {rows.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`board-stay-${s.id}`}>
                    <div className="flex-1"><div className="font-medium text-slate-800">{petName(s.pet_id)}</div><div className="text-xs text-slate-400">{fdate(s.check_in)} → {fdate(s.expected_check_out)} · {inr(s.rate || 0)}</div></div>
                    <button title="Context" data-testid={`ctx-open-${s.id}`} onClick={() => setCtx({ petId: s.pet_id, vertical: "boarding" })} className="p-1 rounded-md hover:bg-sky-50 text-sky-600"><PawPrint className="h-4 w-4" /></button>
                    <StatusBadge status={s.status} />
                    {canEdit && s.status !== "checked_out" && s.status !== "checked_in" && <Button size="sm" variant="outline" onClick={() => checkIn.mutate(s.id)} data-testid={`board-checkin-${s.id}`} className="h-7 gap-1 text-xs"><LogIn className="h-3 w-3" /> Check in</Button>}
                    {canEdit && s.status === "checked_in" && <Button size="sm" variant="outline" onClick={() => checkOut.mutate(s.id)} data-testid={`board-checkout-row-${s.id}`} className="h-7 gap-1 text-xs"><LogOut className="h-3 w-3" /> Check out</Button>}
                    {canEdit && ["reserved", "checked_in"].includes(s.status) && (s.booking_id ? <ReallocateButton bookingId={s.booking_id} onDone={refresh} /> : <StayRelocateButton stay={s} />)}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <FormModal open={resOpen} onOpenChange={setResOpen} title="New Reservation" testid="board-reservation-modal"
        fields={[
          { name: "pet_id", label: "Pet", type: "select", options: petOpts, full: true },
          { name: "resource_id", label: "Resource", type: "select", options: resOpts, full: true },
          { name: "check_in", label: "Check-in", type: "date" },
          { name: "expected_check_out", label: "Expected check-out", type: "date" },
          { name: "rate", label: "Rate (₹/night)", type: "number" },
          { name: "notes", label: "Notes", type: "textarea", full: true },
        ]} onSubmit={(v) => createStay.mutateAsync(v)} submitLabel="Reserve" />

      <FormModal open={kennelOpen} onOpenChange={setKennelOpen} title="Add Resource" testid="board-resource-modal"
        initial={{ type: "kennel", capacity: 1 }}
        fields={[
          { name: "name", label: "Name", placeholder: "Kennel 1", full: true },
          { name: "type", label: "Type", type: "select", options: [{ value: "kennel", label: "Kennel" }, { value: "room", label: "Room" }, { value: "suite", label: "Suite" }] },
          { name: "capacity", label: "Capacity (pets)", type: "number", hint: "Pets of the same customer can share up to this number.",
            validate: (x) => (Number(x) >= 1 && Number(x) <= 50 ? "" : "Capacity must be between 1 and 50") },
        ]} onSubmit={(v) => addKennel.mutateAsync(v)} submitLabel="Add" />
      {ctx && <ContextPanel open={!!ctx} onOpenChange={(o) => !o && setCtx(null)} customerId={ctx.customerId} petId={ctx.petId} vertical="boarding" />}
    </div>
  );
}
